import { useState, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheck, faSquare } from '@fortawesome/free-solid-svg-icons';
import Button from './Button';
import { LangContext, LangContextType, ToastContext, ToastContextType, LoadingContext, LoadingContextType } from '../utilities/customHooks';
import { usePasswordValidation } from '../utilities/usePasswordValidation';
import { changePassword } from '../services/userService';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ModalState = 'initial' | 'loading' | 'success' | 'error';

function PasswordChangeModal({ isOpen, onClose, onSuccess }: PasswordChangeModalProps) {
  const { langState } = useContext(LangContext) as LangContextType;
  const { showToast } = useContext(ToastContext) as ToastContextType;
  const { showLoading, hideLoading } = useContext(LoadingContext) as LoadingContextType;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [state, setState] = useState<ModalState>('initial');
  const [errorMessage, setErrorMessage] = useState('');

  const passwordValidation = usePasswordValidation(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';
  const canSubmit = currentPassword !== '' && passwordValidation.isValid && passwordsMatch;

  const handleChangePassword = async () => {
    if (!canSubmit) return;

    setState('loading');
    setErrorMessage('');
    showLoading();

    try {
      const response = await changePassword(currentPassword, newPassword);
      hideLoading();

      if (response.success) {
        setState('success');
        showToast({
          toastType: 'success',
          message: langState === 'fr'
            ? 'Mot de passe mis à jour avec succès!'
            : 'Password updated successfully!',
          autoDisappearTimeInSeconds: 5,
        });

        // Call onSuccess callback and close modal after 1.5s
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      } else {
        setState('error');
        setErrorMessage(response.message || (langState === 'fr'
          ? 'Échec de la mise à jour du mot de passe'
          : 'Failed to update password'));
      }
    } catch (error: any) {
      hideLoading();
      setState('error');
      setErrorMessage(langState === 'fr'
        ? 'Une erreur s\'est produite. Veuillez réessayer.'
        : 'An error occurred. Please try again.');
    }
  };

  const handleClose = () => {
    setState('initial');
    setErrorMessage('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const ValidationCheckItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center space-x-2 text-sm">
      <FontAwesomeIcon
        icon={met ? faCheck : faSquare}
        className={met ? 'text-cGreen' : 'text-gray-400'}
      />
      <span className={met ? 'text-gray-700' : 'text-gray-500'}>{text}</span>
    </div>
  );

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) {
      handleChangePassword();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent>
        <DialogTitle className="text-xl font-semibold text-primaryDark">
          {langState === 'fr' ? 'Changer votre mot de passe' : 'Change Your Password'}
        </DialogTitle>

        {/* Content */}
        <form onSubmit={handleFormSubmit} className="flex flex-col space-y-4">
          {state === 'success' ? (
            <div className="flex flex-col items-center py-8 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faCheck} className="text-cGreen" size="2x" />
              </div>
              <p className="text-center text-cGreen font-semibold">
                {langState === 'fr'
                  ? 'Mot de passe mis à jour!'
                  : 'Password updated!'}
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                {langState === 'fr'
                  ? 'Mettez à jour votre mot de passe pour répondre à nos exigences de sécurité.'
                  : 'Update your password to meet our security requirements.'}
              </p>

              {/* Current Password */}
              <div>
                <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-2">
                  {langState === 'fr' ? 'Mot de passe actuel' : 'Current Password'}
                </label>
                <div className="relative">
                  <input
                    id="current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  >
                    <FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                  {langState === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder={langState === 'fr' ? 'Entrez le nouveau mot de passe' : 'Enter new password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              {/* Validation Checklist */}
              {newPassword && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {langState === 'fr' ? 'Le mot de passe doit contenir:' : 'Password must contain:'}
                  </p>
                  <ValidationCheckItem
                    met={passwordValidation.hasMinLength}
                    text={langState === 'fr' ? 'Au moins 12 caractères' : 'At least 12 characters'}
                  />
                  <ValidationCheckItem
                    met={passwordValidation.hasUppercase}
                    text={langState === 'fr' ? 'Lettre majuscule (A-Z)' : 'Uppercase letter (A-Z)'}
                  />
                  <ValidationCheckItem
                    met={passwordValidation.hasLowercase}
                    text={langState === 'fr' ? 'Lettre minuscule (a-z)' : 'Lowercase letter (a-z)'}
                  />
                  <ValidationCheckItem
                    met={passwordValidation.hasNumber}
                    text={langState === 'fr' ? 'Chiffre (0-9)' : 'Number (0-9)'}
                  />
                  <ValidationCheckItem
                    met={passwordValidation.hasSpecialChar}
                    text={langState === 'fr' ? 'Caractère spécial (!@#$%...)' : 'Special character (!@#$%...)'}
                  />
                </div>
              )}

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                  {langState === 'fr' ? 'Confirmer le nouveau mot de passe' : 'Confirm New Password'}
                </label>
                <div className="relative">
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                      confirmPassword && !passwordsMatch ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={langState === 'fr' ? 'Confirmez le mot de passe' : 'Confirm password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {confirmPassword && !passwordsMatch && (
                  <p className="text-xs text-red-600 mt-1">
                    {langState === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              {/* Error message */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}

              {/* Update button */}
              <Button
                buttonText={
                  state === 'loading'
                    ? (langState === 'fr' ? 'Mise à jour...' : 'Updating...')
                    : (langState === 'fr' ? 'Mettre à jour le mot de passe' : 'Update Password')
                }
                buttonType="primaryFull"
                actionHandler={handleChangePassword}
                disabled={!canSubmit || state === 'loading'}
              />

              {/* Cancel link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-sm text-gray-600 hover:text-primary transition underline"
                >
                  {langState === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
              </div>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PasswordChangeModal;
